import { FC, MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Link, Modal, Svg, Text, Tooltip } from '@hopskipdrive/react-components';
import { useFlags } from '@rideiq/contexts/LDContext';
import { Button } from '@rideiq/shared/components/Button';
import { colorPalette } from '@rideiq/shared/components/variables';

import { EditLocationModal } from '../EditLocationModal';
import { DetailDescription, LocationContainer, MustBeMetLabel } from '../styles';

import { RiderLocation } from './Location.types';

interface LocationProps {
  riderSlug: string;
  location: RiderLocation;
  associatedLocations?: RiderLocation[];
  removeLocation: (locationId: number) => Promise<boolean>;
  editLocation: (data: RiderLocationFormData, locationDetails: RiderLocation) => Promise<boolean>;
  isRiderActive?: boolean;
}

export const Location: FC<LocationProps> = ({
  riderSlug,
  location,
  associatedLocations,
  removeLocation,
  editLocation,
  isRiderActive,
}) => {
  const navigate = useNavigate();
  const { iepStructuredNotesEnabled } = useFlags();
  const useNewForm = iepStructuredNotesEnabled;
  const [isModalOpen, setIsModalOpen] = useState<'edit' | 'delete' | false>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const addressStreet = location?.address.split(',').slice(0, 1).join(',');
  const addressProvince = location?.address.split(',').slice(1).join(',');

  const handleLocationNameClick = (event: MouseEvent<HTMLElement>) => {
    if (useNewForm) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      navigate(`/riders/${riderSlug}/edit?section=locations`);
    } else {
      setIsModalOpen('edit');
    }
  };

  const handleLocationEdit = async (data, selectedLocation) => {
    setIsLoading(true);
    const response = await editLocation(data, selectedLocation);

    response && setIsModalOpen(false);
    setIsLoading(false);
  };

  const handleLocationDelete = async () => {
    setIsLoading(true);
    const response = await removeLocation(location.id);

    response && setIsModalOpen(false);
    setIsLoading(false);
  };

  return (
    <>
      <LocationContainer data-testid="associated-location">
        <Svg height="13px" icon={location.locationType} data-testid={`${location.locationType}-icon`} />
        <Box ml={2}>
          {isRiderActive ? (
            <Link
              component={useNewForm ? 'a' : 'button'}
              href={useNewForm ? `/riders/${riderSlug}/edit?section=locations` : undefined}
              type={useNewForm ? undefined : 'button'}
              fontWeight={500}
              data-testid="location-name"
              color="primary"
              onClick={handleLocationNameClick}
              sx={{
                display: 'inline-block',
                '&:focus-visible': {
                  outline: `2px solid ${colorPalette.blue40}`,
                  outlineOffset: '2px',
                },
              }}
            >
              {location?.name ? (
                location?.name
              ) : (
                <>
                  <Box component="span" display="block">
                    {addressStreet}
                  </Box>
                  <Box component="span" display="block">
                    {addressProvince}
                  </Box>
                </>
              )}
            </Link>
          ) : (
            <Tooltip title="Rider locations cannot be added or edited when they are paused">
              <div data-testid="location-name-container">
                <Link disabled={!isRiderActive} fontWeight={500} data-testid="location-name" color="primary">
                  {location?.name || (
                    <>
                      <div>{addressStreet}</div>
                      <div>{addressProvince}</div>
                    </>
                  )}
                </Link>
              </div>
            </Tooltip>
          )}
          {location?.schedulingDetails && (
            <DetailDescription>
              <Svg icon="info" width="16px" />
              {location?.schedulingDetails}
            </DetailDescription>
          )}
          <Box display="flex">
            {location?.primary ? (
              <Text sx={{ mr: '.5rem' }} fontWeight={500} textColor="secondaryText">
                Primary
              </Text>
            ) : null}
            {location?.mustBeMet ? (
              <MustBeMetLabel variant="outlined" icon={<Svg icon="handoff" width="16px" />} label="MBM" />
            ) : null}
          </Box>
          {location?.name ? (
            <>
              <Text fontWeight={300} textColor="text">
                {addressStreet}
              </Text>
              <Text fontWeight={300} textColor="text">
                {addressProvince}
              </Text>
            </>
          ) : null}
        </Box>
      </LocationContainer>

      {!useNewForm && isModalOpen === 'edit' && (
        <EditLocationModal
          isOpen={true}
          location={location}
          associatedLocations={associatedLocations}
          onCancel={() => setIsModalOpen(false)}
          onDelete={() => setIsModalOpen('delete')}
          onSubmit={handleLocationEdit}
          isLoading={isLoading}
          riderSlug={riderSlug}
        />
      )}
      {!useNewForm && (
        <DeleteModal
          isOpen={isModalOpen === 'delete'}
          onCancel={() => setIsModalOpen(false)}
          onDelete={handleLocationDelete}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

const DeleteModal = ({ isOpen, isLoading, onCancel, onDelete }) => {
  return (
    <Modal maxWidth="sm" open={isOpen} data-testid="delete-associated-location-modal">
      <Modal.Title>Delete Location</Modal.Title>
      <Modal.Content>
        <Text variant="body2" fontWeight={300} textColor="secondaryText">
          {
            "It will be removed from the rider's list of locations, but any existing rides to this location will not be affected."
          }
        </Text>
      </Modal.Content>
      <Modal.Actions>
        <Button color="tertiary" loading={isLoading} onClick={onCancel}>
          Cancel
        </Button>
        <Button color="primary" onClick={onDelete} loading={isLoading}>
          {isLoading ? 'Deleting...' : 'Delete'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export type { RiderLocation } from './Location.types';
